import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Layout from '../components/dashboard/Layout';
import MovementModal from '../components/MovementModal';
import { labelsAPI, assetsAPI } from '../services/api';
import { Scan, Search, Package, X, Clock, Filter, AlertCircle } from 'lucide-react';

const BarcodeScanPageImproved = () => {
  const navigate = useNavigate();
  const [searchInput, setSearchInput] = useState('');
  const [scannedAsset, setScannedAsset] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [showMovementModal, setShowMovementModal] = useState(false);
  
  // New features
  const [allAssets, setAllAssets] = useState([]);
  const [suggestions, setSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [recentScans, setRecentScans] = useState([]);
  const [searchMode, setSearchMode] = useState('all'); // 'all', 'barcode', 'serial', 'inventory'

  useEffect(() => {
    loadAssets();
    loadRecentScans();
  }, []);

  const loadAssets = async () => {
    try {
      const response = await assetsAPI.getAll();
      setAllAssets(response.data);
    } catch (error) {
      console.error('Failed to load assets:', error);
    }
  };

  const loadRecentScans = () => {
    const stored = localStorage.getItem('recentScans');
    if (stored) {
      setRecentScans(JSON.parse(stored));
    }
  };

  const saveToRecentScans = (asset) => {
    const newRecent = [
      { ...asset, scannedAt: new Date().toISOString() },
      ...recentScans.filter(r => r.id !== asset.id)
    ].slice(0, 5); // Keep last 5
    
    setRecentScans(newRecent);
    localStorage.setItem('recentScans', JSON.stringify(newRecent));
  };

  const handleSearchInputChange = (value) => {
    setSearchInput(value);
    
    if (value.length >= 2) {
      // Filter suggestions based on search mode
      const filtered = allAssets.filter(asset => {
        const searchLower = value.toLowerCase();
        
        if (searchMode === 'barcode') {
          return asset.barcode?.toLowerCase().includes(searchLower);
        } else if (searchMode === 'serial') {
          return asset.serial_number?.toLowerCase().includes(searchLower);
        } else if (searchMode === 'inventory') {
          return asset.inventory_number?.toLowerCase().includes(searchLower);
        } else {
          // Search all fields
          return (
            asset.barcode?.toLowerCase().includes(searchLower) ||
            asset.serial_number?.toLowerCase().includes(searchLower) ||
            asset.inventory_number?.toLowerCase().includes(searchLower) ||
            asset.brand?.toLowerCase().includes(searchLower) ||
            asset.model?.toLowerCase().includes(searchLower)
          );
        }
      }).slice(0, 10); // Max 10 suggestions
      
      setSuggestions(filtered);
      setShowSuggestions(true);
    } else {
      setSuggestions([]);
      setShowSuggestions(false);
    }
  };

  const handleSearch = async (e) => {
    e.preventDefault();
    if (!searchInput.trim()) return;

    setLoading(true);
    setError('');
    setScannedAsset(null);
    setShowSuggestions(false);

    try {
      // Try to find by barcode first
      let asset = null;
      
      try {
        const response = await labelsAPI.scanBarcode(searchInput.trim());
        asset = response.data;
      } catch (barcodeError) {
        // If barcode not found, search in all assets manually
        const found = allAssets.find(a => 
          a.barcode?.toLowerCase() === searchInput.trim().toLowerCase() ||
          a.serial_number?.toLowerCase() === searchInput.trim().toLowerCase() ||
          a.inventory_number?.toLowerCase() === searchInput.trim().toLowerCase() ||
          (a.brand + ' ' + a.model).toLowerCase().includes(searchInput.trim().toLowerCase())
        );
        
        if (found) {
          asset = found;
        }
      }
      
      if (asset) {
        setScannedAsset(asset);
        saveToRecentScans(asset);
      } else {
        setError('Asset tidak ditemukan. Coba gunakan saran pencarian di bawah atau ubah mode pencarian.');
      }
    } catch (err) {
      console.error('Search error:', err);
      setError('Terjadi error saat mencari asset. Silakan coba lagi.');
    } finally {
      setLoading(false);
    }
  };

  const handleSuggestionClick = (asset) => {
    setScannedAsset(asset);
    setSearchInput('');
    setShowSuggestions(false);
    saveToRecentScans(asset);
  };

  const handleRecentClick = (asset) => {
    setScannedAsset(asset);
  };

  const handleQuickMovement = () => {
    setShowMovementModal(true);
  };

  const handleViewDetails = () => {
    if (scannedAsset) {
      navigate(`/assets/${scannedAsset.id}`);
    }
  };

  const resetScan = () => {
    setSearchInput('');
    setScannedAsset(null);
    setError('');
    setSuggestions([]);
    setShowSuggestions(false);
  };

  const getStatusColor = (status) => {
    const colors = {
      ready: 'bg-green-500/10 text-green-600 border-green-500/30',
      rented: 'bg-blue-500/10 text-blue-600 border-blue-500/30',
      on_duty: 'bg-purple-500/10 text-purple-600 border-purple-500/30',
      maintenance: 'bg-yellow-500/10 text-yellow-600 border-yellow-500/30',
      service: 'bg-orange-500/10 text-orange-600 border-orange-500/30',
      repair: 'bg-red-500/10 text-red-600 border-red-500/30',
      retired: 'bg-gray-500/10 text-gray-600 border-gray-500/30',
      sold: 'bg-pink-500/10 text-pink-600 border-pink-500/30'
    };
    return colors[status] || colors.ready;
  };

  const getStatusLabel = (status) => {
    const labels = {
      ready: '✅ Ready',
      rented: '🔄 Rented',
      on_duty: '💼 On Duty',
      maintenance: '🔧 Maintenance',
      service: '⚠️ Service',
      repair: '🛠️ Repair',
      retired: '🚫 Retired',
      sold: '💰 Terjual'
    };
    return labels[status] || status;
  };

  return (
    <Layout>
      <div className="space-y-6 max-w-5xl mx-auto">
        {/* Header */}
        <div className="text-center">
          <h1 className="text-3xl font-bold text-foreground mb-2">🔍 Cari Asset dengan Mudah</h1>
          <p className="text-muted-foreground">
            Scan barcode atau cari manual dengan berbagai cara
          </p>
        </div>

        {/* Search Mode Selector */}
        <div className="glass-card p-4 rounded-xl border border-border/40">
          <div className="flex items-center space-x-2 mb-3">
            <Filter className="w-4 h-4 text-muted-foreground" />
            <span className="text-sm font-medium text-foreground">Mode Pencarian:</span>
          </div>
          <div className="flex flex-wrap gap-2">
            <button
              onClick={() => setSearchMode('all')}
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                searchMode === 'all'
                  ? 'bg-primary text-primary-foreground'
                  : 'bg-muted text-foreground hover:bg-muted/80'
              }`}
            >
              🔎 Semua Field
            </button>
            <button
              onClick={() => setSearchMode('barcode')}
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                searchMode === 'barcode'
                  ? 'bg-primary text-primary-foreground'
                  : 'bg-muted text-foreground hover:bg-muted/80'
              }`}
            >
              📊 Barcode
            </button>
            <button
              onClick={() => setSearchMode('serial')}
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                searchMode === 'serial'
                  ? 'bg-primary text-primary-foreground'
                  : 'bg-muted text-foreground hover:bg-muted/80'
              }`}
            >
              🔢 Serial Number
            </button>
            <button
              onClick={() => setSearchMode('inventory')}
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                searchMode === 'inventory'
                  ? 'bg-primary text-primary-foreground'
                  : 'bg-muted text-foreground hover:bg-muted/80'
              }`}
            >
              📦 Inventory Number
            </button>
          </div>
        </div>

        {/* Search Input with Suggestions */}
        <div className="glass-card p-8 rounded-xl border border-border/40">
          <form onSubmit={handleSearch} className="space-y-4">
            <div className="relative">
              <Scan className="absolute left-4 top-1/2 transform -translate-y-1/2 w-6 h-6 text-primary" />
              <input
                type="text"
                value={searchInput}
                onChange={(e) => handleSearchInputChange(e.target.value)}
                placeholder={
                  searchMode === 'all' ? 'Ketik apa saja: barcode, serial, inventory, brand...' :
                  searchMode === 'barcode' ? 'Ketik atau scan barcode...' :
                  searchMode === 'serial' ? 'Ketik serial number...' :
                  'Ketik inventory number...'
                }
                className="w-full pl-14 pr-12 py-4 bg-muted/50 border-2 border-border rounded-lg text-foreground text-lg focus:outline-none focus:border-primary transition-colors"
                autoFocus
                disabled={loading}
                onFocus={() => searchInput.length >= 2 && setShowSuggestions(true)}
              />
              {searchInput && (
                <button
                  type="button"
                  onClick={() => {
                    setSearchInput('');
                    setSuggestions([]);
                    setShowSuggestions(false);
                  }}
                  className="absolute right-4 top-1/2 transform -translate-y-1/2 text-muted-foreground hover:text-foreground"
                >
                  <X className="w-5 h-5" />
                </button>
              )}
              
              {/* Suggestions Dropdown */}
              {showSuggestions && suggestions.length > 0 && (
                <div className="absolute top-full left-0 right-0 mt-2 bg-background border-2 border-border rounded-lg shadow-lg z-10 max-h-96 overflow-y-auto">
                  <div className="p-2">
                    <p className="text-xs text-muted-foreground px-3 py-2">
                      {suggestions.length} hasil ditemukan:
                    </p>
                    {suggestions.map((asset) => (
                      <button
                        key={asset.id}
                        type="button"
                        onClick={() => handleSuggestionClick(asset)}
                        className="w-full text-left p-3 hover:bg-muted rounded-lg transition-colors"
                      >
                        <div className="flex items-center justify-between">
                          <div>
                            <p className="font-semibold text-foreground">
                              {asset.brand} {asset.model}
                            </p>
                            <div className="flex space-x-3 text-xs text-muted-foreground mt-1">
                              <span>📊 {asset.barcode}</span>
                              <span>🔢 {asset.serial_number}</span>
                              {asset.inventory_number && <span>📦 {asset.inventory_number}</span>}
                            </div>
                          </div>
                          <span className={`px-2 py-1 rounded text-xs font-medium border ${getStatusColor(asset.status)}`}>
                            {asset.status}
                          </span>
                        </div>
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </div>
            
            <button
              type="submit"
              disabled={loading || !searchInput.trim()}
              className="w-full px-6 py-4 bg-primary text-primary-foreground rounded-lg hover:bg-primary/90 transition-colors flex items-center justify-center space-x-2 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <Search className="w-5 h-5" />
              <span>{loading ? 'Mencari...' : 'Cari Asset'}</span>
            </button>
          </form>

          {/* Instructions */}
          <div className="mt-6 space-y-3">
            <div className="p-4 bg-blue-500/10 border border-blue-500/30 rounded-lg">
              <p className="text-sm text-blue-600">
                💡 <strong>Tips:</strong> Ketik minimal 2 karakter untuk melihat saran. 
                Mode "Semua Field" mencari di barcode, serial number, inventory number, brand, dan model.
              </p>
            </div>
            
            {searchMode !== 'all' && (
              <div className="p-4 bg-green-500/10 border border-green-500/30 rounded-lg">
                <p className="text-sm text-green-600">
                  ✨ <strong>Mode Aktif:</strong> Pencarian difokuskan pada {
                    searchMode === 'barcode' ? 'Barcode' :
                    searchMode === 'serial' ? 'Serial Number' :
                    'Inventory Number'
                  } saja. Ubah mode untuk mencari field lain.
                </p>
              </div>
            )}
          </div>
        </div>

        {/* Recent Scans */}
        {recentScans.length > 0 && !scannedAsset && (
          <div className="glass-card p-6 rounded-xl border border-border/40">
            <div className="flex items-center space-x-2 mb-4">
              <Clock className="w-5 h-5 text-secondary" />
              <h3 className="text-lg font-semibold text-foreground">Riwayat Scan Terakhir</h3>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {recentScans.map((asset) => (
                <button
                  key={asset.id}
                  onClick={() => handleRecentClick(asset)}
                  className="p-4 text-left bg-muted/50 hover:bg-muted rounded-lg transition-colors border border-border"
                >
                  <div className="flex justify-between items-start mb-2">
                    <p className="font-semibold text-foreground">
                      {asset.brand} {asset.model}
                    </p>
                    <span className={`px-2 py-1 rounded text-xs font-medium border ${getStatusColor(asset.status)}`}>
                      {asset.status}
                    </span>
                  </div>
                  <p className="text-xs text-muted-foreground">
                    📊 {asset.barcode} • 🔢 {asset.serial_number}
                  </p>
                  <p className="text-xs text-muted-foreground mt-1">
                    ⏰ {new Date(asset.scannedAt).toLocaleString('id-ID')}
                  </p>
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Error Message */}
        {error && (
          <div className="glass-card p-6 rounded-xl border border-destructive/40 bg-destructive/10">
            <div className="flex items-start space-x-3 mb-4">
              <AlertCircle className="w-5 h-5 text-destructive flex-shrink-0 mt-0.5" />
              <p className="text-destructive">{error}</p>
            </div>
            {suggestions.length > 0 && (
              <div className="mb-4">
                <p className="text-sm font-medium text-foreground mb-2">Mungkin yang Anda cari:</p>
                <div className="space-y-2">
                  {suggestions.slice(0, 3).map((asset) => (
                    <button
                      key={asset.id}
                      onClick={() => handleSuggestionClick(asset)}
                      className="w-full p-3 text-left bg-background hover:bg-muted rounded-lg transition-colors border border-border"
                    >
                      <p className="font-medium text-foreground">
                        {asset.brand} {asset.model}
                      </p>
                      <p className="text-xs text-muted-foreground">
                        {asset.barcode} • {asset.serial_number}
                      </p>
                    </button>
                  ))}
                </div>
              </div>
            )}
            <button
              onClick={resetScan}
              className="w-full px-4 py-2 bg-muted text-foreground rounded-lg hover:bg-muted/80"
            >
              Coba Lagi
            </button>
          </div>
        )}

        {/* Scanned Asset Result */}
        {scannedAsset && (
          <div className="glass-card p-6 rounded-xl border-2 border-primary/40 bg-gradient-to-br from-primary/5 to-secondary/5 space-y-6">
            <div className="flex items-center justify-between">
              <h2 className="text-2xl font-bold text-foreground">✅ Asset Ditemukan!</h2>
              <span className={`px-4 py-2 rounded-lg text-sm font-medium border ${getStatusColor(scannedAsset.status)}`}>
                {getStatusLabel(scannedAsset.status)}
              </span>
            </div>

            {/* Asset Info */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="space-y-3">
                <div>
                  <p className="text-sm text-muted-foreground">Brand & Model</p>
                  <p className="text-lg font-semibold text-foreground">
                    {scannedAsset.brand} {scannedAsset.model}
                  </p>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Barcode</p>
                  <p className="text-foreground font-mono bg-background px-3 py-1 rounded">{scannedAsset.barcode}</p>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Serial Number</p>
                  <p className="text-foreground font-mono bg-background px-3 py-1 rounded">{scannedAsset.serial_number}</p>
                </div>
                {scannedAsset.inventory_number && (
                  <div>
                    <p className="text-sm text-muted-foreground">Inventory Number</p>
                    <p className="text-foreground font-mono bg-background px-3 py-1 rounded">{scannedAsset.inventory_number}</p>
                  </div>
                )}
              </div>

              <div className="space-y-3">
                <div>
                  <p className="text-sm text-muted-foreground">Kategori</p>
                  <p className="text-foreground capitalize">{scannedAsset.category}</p>
                </div>
                {scannedAsset.current_location && (
                  <div>
                    <p className="text-sm text-muted-foreground">Lokasi Saat Ini</p>
                    <p className="text-foreground">{scannedAsset.current_location}</p>
                  </div>
                )}
                {scannedAsset.processor && (
                  <div>
                    <p className="text-sm text-muted-foreground">Spesifikasi</p>
                    <p className="text-foreground text-sm">
                      {scannedAsset.processor} | {scannedAsset.ram} | {scannedAsset.storage}
                    </p>
                  </div>
                )}
              </div>
            </div>

            {/* Action Buttons */}
            <div className="flex flex-col sm:flex-row gap-3 pt-4 border-t border-border">
              <button
                onClick={handleQuickMovement}
                className="flex-1 px-6 py-3 bg-primary text-primary-foreground rounded-lg hover:bg-primary/90 transition-colors flex items-center justify-center space-x-2"
              >
                <Scan className="w-5 h-5" />
                <span>Catat Perpindahan</span>
              </button>
              <button
                onClick={handleViewDetails}
                className="flex-1 px-6 py-3 bg-secondary text-secondary-foreground rounded-lg hover:bg-secondary/90 transition-colors flex items-center justify-center space-x-2"
              >
                <Package className="w-5 h-5" />
                <span>Lihat Detail Lengkap</span>
              </button>
            </div>

            <button
              onClick={resetScan}
              className="w-full px-4 py-2 text-muted-foreground hover:text-foreground transition-colors"
            >
              🔍 Cari Asset Lain
            </button>
          </div>
        )}

        {/* Movement Modal */}
        {showMovementModal && scannedAsset && (
          <MovementModal
            assetId={scannedAsset.id}
            onClose={() => setShowMovementModal(false)}
            onSuccess={() => {
              setShowMovementModal(false);
              // Refresh scanned asset data
              handleSearch({ preventDefault: () => {} });
            }}
          />
        )}
      </div>
    </Layout>
  );
};

export default BarcodeScanPageImproved;
